// src/pages/dashboard/feature/FilterPanel.jsx
import React, { useState } from "react";
import {
    Paper,
    Collapse,
    Stack,
    Chip,
    Menu,
    MenuItem,
    Typography,
    Box,
    IconButton
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import PlaceIcon from "@mui/icons-material/Place";
import ParkIcon from "@mui/icons-material/Park";
import ClassIcon from "@mui/icons-material/Class";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CloseIcon from '@mui/icons-material/Close';

// Import styles dari file terpisah
import { getFilterChipStyles, getResetChipStyles } from "./FilterChipStyles";
import { menuStyles } from "./MenuStyles";

function FilterPanel({
    filterOpen,
    paperShadow,
    selectedFilters,
    updateFilter,
    clearAllFilters
}) {
    // State untuk menu dropdown masing-masing filter
    const [anchorEls, setAnchorEls] = useState({
        wilayah: null,
        kepalaKebun: null,
        statusLokasi: null,
        jenisBibit: null,
        kelasBibit: null
    });

    // Data dummy untuk dropdown
    const filterOptions = {
        wilayah: [
            { value: "AW24", label: "AW24" },
            { value: "AW25", label: "AW25" },
            { value: "CGT", label: "CGT" },
        ],
        kepalaKebun: [
            { value: "SUSILO", label: "SUSILO" },
            { value: "MULYADI", label: "MULYADI" },
            { value: "SUHARNO", label: "SUHARNO" },
            { value: "HARGO", label: "HARGO" },
            { value: "KUSWARA", label: "KUSWARA" }
        ],
        statusLokasi: [
            { value: "NFBK", label: "NFBK" },
            { value: "NFST", label: "NFST" },
            { value: "NLFC", label: "NLFC" },
            { value: "NFFC", label: "NFFC" },
            { value: "NFSC", label: "NFSC" },
            { value: "NFSR", label: "NFSR" },
            { value: "NFBB", label: "NFBB" }
        ],
        jenisBibit: [
            { value: "C", label: "Crown" },
            { value: "S", label: "Sucker" }
        ],
        kelasBibit: [
            { value: "B", label: "Besar" },
            { value: "S", label: "Sedang" },
            { value: "K", label: "Kecil" }
        ]
    };

    // Handler untuk membuka menu dropdown
    const handleMenuOpen = (event, filterKey) => {
        setAnchorEls({
            ...anchorEls,
            [filterKey]: event.currentTarget
        });
    };

    // Handler untuk menutup menu dropdown
    const handleMenuClose = (filterKey) => {
        setAnchorEls({
            ...anchorEls,
            [filterKey]: null
        });
    };

    // Handler untuk memilih item dari dropdown
    const handleFilterSelect = (filterKey, value, label) => {
        updateFilter(filterKey, { value, label });
        handleMenuClose(filterKey);
        console.log(`Filter ${filterKey} dipilih:`, { value, label });
    };

    // Handler untuk menghapus filter yang dipilih
    const handleFilterRemove = (filterKey) => {
        updateFilter(filterKey, null);
        console.log(`Filter ${filterKey} dihapus`);
    };

    // Handler untuk menghapus semua filter
    const handleClearAllFiltersLocal = () => {
        clearAllFilters();
        console.log("Semua filter dihapus");
    };

    // Fungsi untuk mendapatkan deleteIcon berdasarkan status filter
    const getDeleteIcon = (filterKey) => {
        const isOpen = Boolean(anchorEls[filterKey]);
        const hasSelection = selectedFilters[filterKey] !== null;
        
        if (hasSelection) {
            return <CloseIcon />;
        }
        
        return (
            <ArrowDropDownIcon
                sx={{
                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                }}
            />
        );
    };

    // Handler untuk klik delete icon
    const handleDeleteIconClick = (e, filterKey) => {
        e.stopPropagation(); // Mencegah event bubble ke onClick Chip
        
        if (selectedFilters[filterKey]) {
            // Jika ada pilihan, hapus pilihan
            handleFilterRemove(filterKey);
        } else {
            // Jika tidak ada pilihan, buka/tutup menu
            handleMenuOpen(e, filterKey);
        }
    };

    // Handler untuk klik Chip
    const handleChipClick = (e, filterKey) => {
        // Jika Chip diklik, selalu buka menu
        handleMenuOpen(e, filterKey);
    };

    // Fungsi untuk menentukan apakah chip jenis dan kelas bibit harus ditampilkan
const shouldShowJenisKelasBibit = () => {
  if (!selectedFilters.statusLokasi) return true;

  return ["NFFC"].includes(selectedFilters.statusLokasi.value);
};


    const showJenisKelasBibit = shouldShowJenisKelasBibit();

    return (
        <Collapse 
            in={filterOpen}
            timeout={300}
            easing={{enter: 'cubic-bezier(0.4, 0, 0.2, 1)', exit: 'cubic-bezier(0.4, 0, 0.2, 1)'}}
            sx={{ width: '100%', overflow: 'hidden', mb: 0 }}
        >
            <Paper
                sx={{
                    p: 0,
                    mb: 3,
                    borderRadius: 2,
                    boxShadow: 0,
                    transition: 'all 0.3s ease',
                    backgroundColor: "transparent"
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {/* Chip Kode Wilayah */}
                    <Box>
                        <Chip
                            title="Kode Wilayah"
                            icon={<LocationOnIcon />}
                            label={selectedFilters.wilayah ? selectedFilters.wilayah.label : "Kode Wilayah"}
                            deleteIcon={getDeleteIcon('wilayah')}
                            onDelete={(e) => handleDeleteIconClick(e, 'wilayah')}
                            onClick={(e) => handleChipClick(e, 'wilayah')}
                            color={selectedFilters.wilayah ? "primary" : "default"}
                            sx={getFilterChipStyles(!!selectedFilters.wilayah, paperShadow)}
                        />
                        <Menu
                            anchorEl={anchorEls.wilayah}
                            open={Boolean(anchorEls.wilayah)}
                            onClose={() => handleMenuClose('wilayah')}
                            PaperProps={{
                                sx: menuStyles.paper(paperShadow)
                            }}
                        >
                            {filterOptions.wilayah.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    onClick={() => handleFilterSelect('wilayah', option.value, option.label)}
                                    selected={selectedFilters.wilayah?.value === option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    {/* Chip Kepala Kebun */}
                    <Box>
                        <Chip
                            title="Kepala Kebun"
                            icon={<PersonIcon />}
                            label={selectedFilters.kepalaKebun ? selectedFilters.kepalaKebun.label : "Kepala Kebun"}
                            deleteIcon={getDeleteIcon('kepalaKebun')}
                            onDelete={(e) => handleDeleteIconClick(e, 'kepalaKebun')}
                            onClick={(e) => handleChipClick(e, 'kepalaKebun')}
                            color={selectedFilters.kepalaKebun ? "primary" : "default"}
                            sx={getFilterChipStyles(!!selectedFilters.kepalaKebun, paperShadow)}
                        />
                        <Menu
                            anchorEl={anchorEls.kepalaKebun}
                            open={Boolean(anchorEls.kepalaKebun)}
                            onClose={() => handleMenuClose('kepalaKebun')}
                            PaperProps={{
                                sx: menuStyles.paper(paperShadow)
                            }}
                        >
                            {filterOptions.kepalaKebun.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    onClick={() => handleFilterSelect('kepalaKebun', option.value, option.label)}
                                    selected={selectedFilters.kepalaKebun?.value === option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    {/* Chip Status Lokasi */}
                    <Box>
                        <Chip
                            title="Status Lokasi"
                            icon={<PlaceIcon />}
                            label={selectedFilters.statusLokasi ? selectedFilters.statusLokasi.label : "Status Lokasi"}
                            deleteIcon={getDeleteIcon('statusLokasi')}
                            onDelete={(e) => handleDeleteIconClick(e, 'statusLokasi')}
                            onClick={(e) => handleChipClick(e, 'statusLokasi')}
                            color={selectedFilters.statusLokasi ? "primary" : "default"}
                            sx={getFilterChipStyles(!!selectedFilters.statusLokasi, paperShadow)}
                        />
                        <Menu
                            anchorEl={anchorEls.statusLokasi}
                            open={Boolean(anchorEls.statusLokasi)}
                            onClose={() => handleMenuClose('statusLokasi')}
                            PaperProps={{
                                sx: menuStyles.paper(paperShadow)
                            }}
                        >
                            {filterOptions.statusLokasi.map((option) => (
                                <MenuItem
                                    key={option.value}
                                    onClick={() => handleFilterSelect('statusLokasi', option.value, option.label)}
                                    selected={selectedFilters.statusLokasi?.value === option.value}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>

                    {/* Chip Jenis Bibit - Tampilkan hanya jika showJenisKelasBibit = true */}
                    {showJenisKelasBibit && (
                        <Box>
                            <Chip
                                title="Jenis Bibit"
                                icon={<ParkIcon />}
                                label={selectedFilters.jenisBibit ? selectedFilters.jenisBibit.label : "Jenis Bibit"}
                                deleteIcon={getDeleteIcon('jenisBibit')}
                                onDelete={(e) => handleDeleteIconClick(e, 'jenisBibit')}
                                onClick={(e) => handleChipClick(e, 'jenisBibit')}
                                color={selectedFilters.jenisBibit ? "primary" : "default"}
                                sx={getFilterChipStyles(!!selectedFilters.jenisBibit, paperShadow)}
                            />
                            <Menu
                                anchorEl={anchorEls.jenisBibit}
                                open={Boolean(anchorEls.jenisBibit)}
                                onClose={() => handleMenuClose('jenisBibit')}
                                PaperProps={{
                                    sx: menuStyles.paper(paperShadow)
                                }}
                            >
                                {filterOptions.jenisBibit.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        onClick={() => handleFilterSelect('jenisBibit', option.value, option.label)}
                                        selected={selectedFilters.jenisBibit?.value === option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    )}

                    {/* Chip Kelas Bibit - Tampilkan hanya jika showJenisKelasBibit = true */}
                    {showJenisKelasBibit && (
                        <Box>
                            <Chip
                                title="Kelas Bibit"
                                icon={<ClassIcon />}
                                label={selectedFilters.kelasBibit ? selectedFilters.kelasBibit.label : "Kelas Bibit"}
                                deleteIcon={getDeleteIcon('kelasBibit')}
                                onDelete={(e) => handleDeleteIconClick(e, 'kelasBibit')}
                                onClick={(e) => handleChipClick(e, 'kelasBibit')}
                                color={selectedFilters.kelasBibit ? "primary" : "default"}
                                sx={getFilterChipStyles(!!selectedFilters.kelasBibit, paperShadow)}
                            />
                            <Menu
                                anchorEl={anchorEls.kelasBibit}
                                open={Boolean(anchorEls.kelasBibit)}
                                onClose={() => handleMenuClose('kelasBibit')}
                                PaperProps={{
                                    sx: menuStyles.paper(paperShadow)
                                }}
                            >
                                {filterOptions.kelasBibit.map((option) => (
                                    <MenuItem
                                        key={option.value}
                                        onClick={() => handleFilterSelect('kelasBibit', option.value, option.label)}
                                        selected={selectedFilters.kelasBibit?.value === option.value}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    )}
                </Stack>
                    {Object.values(selectedFilters).some(filter => filter !== null) && (
                        <Chip
                            icon={<CancelOutlinedIcon style={{ color: 'white' }} />}
                            label="Reset Filter"
                            onClick={handleClearAllFiltersLocal}
                            sx={getResetChipStyles(paperShadow)}
                        />
                    )}
                </Stack>
            </Paper>
        </Collapse>
    );
}

export default FilterPanel;